import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { any, find, fromPairs, isEmpty, isNil, toPairs } from 'ramda';
import { isRequired } from '../helpers/validations';

/**
 * @param {*} form
 * @param {function} getMiddleBoolean
 * @param {{ [fieldKey:string]: {
 *  formKey: string, // Key used to access the field value with form.getValues()
 *  errorKey: string, // Key of the rendered input
 *  validatorFunction: (value:any) => boolean|string, // Function used to validate the field value, if a string is returned, it is processed as an error message
 *  getValue: (formObject:any) => any, // Get the final value to validate from the form value returned by form.getValues(formKey)
 *  baseMiddleBooleanKey: string, // key to get the Middle Boolean defining if this field is required by itself (without cross validation)
 *  crossValidationMiddleBooleanKeys: Array<string>  // keys to get the Middle booleans implying that this field is part of a cross validation
 * } }} fieldsMetadata
 * @returns {function} getFieldInputRules // Function to pass to the form input 'rules' property
 */
const useFormFieldsCrossValidation = (
  form,
  getMiddleBoolean,
  fieldsMetadata
) => {
  const [fieldsStatus, setFieldsStatus] = useState({});

  const fieldsToValidate = Object.keys(fieldsMetadata).filter((field) =>
    any(
      Boolean,
      fieldsMetadata[field].crossValidationMiddleBooleanKeys.map((key) =>
        getMiddleBoolean(key)
      )
    )
  );

  const validateField = (field, form) => {
    const metadata = fieldsMetadata[field];
    const formKey = metadata.formKey;
    const errorPath = `${formKey}.${metadata.errorKey}`;
    const formObject = form.getValues(formKey);
    const value = metadata.getValue(formObject);
    const fieldValidation = metadata.validatorFunction(true).validate(value);
    if (typeof fieldValidation === 'string') {
      form.setError(errorPath, { type: 'validate', message: fieldValidation });
    }
    return { formKey, errorPath, value, isValid: fieldValidation === true };
  };

  const useEffectDependencies = fieldsToValidate.map((field) =>
    useWatch({ control: form.control, name: fieldsMetadata[field].formKey })
  );
  useEffect(() => {
    if (fieldsToValidate.length === 0) {
      return;
    }

    const newFieldsStatus = fromPairs(
      fieldsToValidate.map((field) => [field, validateField(field, form)])
    );
    for (const field in newFieldsStatus) {
      const status = newFieldsStatus[field];
      const anotherFieldIsValid =
        find(
          ([otherField, otherFieldStatus]) =>
            otherField !== field && otherFieldStatus.isValid,
          toPairs(newFieldsStatus)
        ) !== undefined;
      status.isRequired = !anotherFieldIsValid;

      if (
        status.isValid ||
        (!status.isRequired && (isNil(status.value) || isEmpty(status.value)))
      ) {
        form.clearErrors(status.errorPath);
      }
    }
    setFieldsStatus(newFieldsStatus);
  }, useEffectDependencies);

  const getFieldInputRules = (field) => {
    const validatorFunction = fieldsMetadata[field].validatorFunction;
    if (fieldsToValidate.includes(field)) {
      const fieldIsRequired = fieldsStatus[field]?.isRequired ?? true;
      return {
        required: fieldIsRequired && isRequired.required,
        validate: validatorFunction(fieldIsRequired).validate
      };
    } else {
      return validatorFunction(
        getMiddleBoolean(fieldsMetadata[field].baseMiddleBooleanKey)
      );
    }
  };

  return getFieldInputRules;
};

export default useFormFieldsCrossValidation;
